import { isDriverNavigatingInspectionMode, isDriverOnAnyWorkflow } from "./uri";
import { htmlEscape, extend, createElement } from "./vanilla";
import { dispatcher, DispatcherEvents } from "./dispatcher";

export enum ToastOrigins {
    "HOS_STATUS_CHANGE" = "hos_status_change",
    "NETWORK_CONNECTION_CHANGE" = "network_connection_change"
}

export interface IToastLink {
    linkText: string;
    linkId: string;
    linkClass?: string;
    linkCallback?: () => void;
    callbackWrapper?: (e) => void;
}

type TToastrType = "error" | "info" | "success" | "warning";
type TToastrDisplayMethod = (
    message: string | HTMLElement | HTMLElement[],
    title?: string | HTMLElement | HTMLElement[],
    link?: IToastLink[],
    optionOverrides?: Partial<IToastrOptions>) => HTMLElement | undefined;

interface IToastrOptions {
    closeOnHover: boolean;
    tapToDismiss: boolean;
    toastAdditionalClass?: string;
    toastId?: string;
    onShown?: () => void;
    onHidden?: () => void;
    extendedTimeOut: number;
    iconClass: TToastrType;
    positionClass: string;
    timeOut: number;
    titleClass: string;
    messageClass: string;
    newestOnTop: boolean;
    target: string;
    preventDuplicates: boolean;
    onclick?: (ev: any) => void;
    origin?: string;
    isImportant: boolean;
    isUserNightMode: boolean;
    allowHtml?: boolean;
}

let containerEl: HTMLElement | null;
let previousToast: string | HTMLElement | HTMLElement[] | undefined;

const createContainer = function(options: IToastrOptions) {
    containerEl = createElement("div", undefined, "toast-container");

    if (options.positionClass.indexOf("bottom") > -1) {
        let menu = document.getElementById("footer-menu");
        if (menu && menu.style.display !== "none") {
            containerEl.classList.add("toast-above-menu");
        }
    }

    let target = document.querySelectorAll(options.target);
    target && target[0].appendChild(containerEl);
};

const setToastrPosition = function(options: IToastrOptions) {
    if (containerEl) {
        containerEl.className = "";
        containerEl.classList.add(options.positionClass);

        if (options.positionClass.indexOf("bottom") > -1) {
            containerEl.classList.add("toast-above-menu");
        }

        if (options.isUserNightMode || window.whitelabel === "lytx") {
            containerEl.classList.add("nightMode");
        }
    }
};

const getDefaultOptions = (): IToastrOptions => ({
    tapToDismiss: true,
    toastId: undefined,
    onShown: undefined,
    onHidden: undefined,
    closeOnHover: true,
    allowHtml: false,

    extendedTimeOut: 1000,
    iconClass: "info",
    positionClass: "toast-top-center",
    timeOut: 5000, // Set timeOut and extendedTimeOut to 0 to make it sticky
    titleClass: "toast-title",
    messageClass: "toast-message",
    target: "body",
    newestOnTop: true,
    preventDuplicates: false,
    isImportant: true,
    isUserNightMode: false
});

const removeToast = function(el: HTMLElement) {
    if (!containerEl || !el) {
        return;
    }
    if (el.remove) {
        el.remove();
    } else {
        // IE 11
        el.parentElement?.removeChild(el);
    }
    previousToast = undefined;
};

const notify = function(
    type: string,
    message: string | HTMLElement | HTMLElement[],
    title?: string | HTMLElement | HTMLElement[],
    link?: IToastLink[],
    optionsOverride?: Partial<IToastrOptions>
) {
    let options: IToastrOptions = { ...getDefaultOptions(), ...optionsOverride };
    let map = { type, message, title, link, options };

    function shouldExit(opt: IToastrOptions, msg: string | HTMLElement | HTMLElement[]): boolean {
        if (!opt.isImportant && (isDriverNavigatingInspectionMode() || isDriverOnAnyWorkflow())) {
            return true;
        }

        if (!opt.preventDuplicates) {
            return false;
        }

        if (msg === previousToast) {
            return true;
        } else {
            previousToast = msg;
            return false;
        }
    }

    if (shouldExit(options, message)) {
        return undefined;
    }

    let iconClass = "toast-" + (map.type || options.iconClass);
    let intervalId: number | null = null;
    let toastEl: HTMLElement | null = createElement("div", "toast", options.toastId);
    let titleEl: HTMLElement | null = createElement("div");
    let messageEl: HTMLElement | null = createElement("div");
    let linkEl: HTMLElement | null = createElement("div");
    let toastLinks: HTMLElement[];

    function setOrigin() {
        !!options.origin && toastEl && toastEl.setAttribute("data-origin", options.origin);
    }

    function setAria() {
        let ariaValue = "";
        switch (iconClass) {
            case "toast-success":
            case "toast-info":
                ariaValue =  "polite";
                break;
            default:
                ariaValue = "assertive";
        }
        toastEl && toastEl.setAttribute("aria-live", ariaValue);
    }

    function stickAround() {
        if (intervalId) {
            clearTimeout(intervalId);
        }
    }

    function logInteraction(e?: Event) {
        e && dispatcher.trigger(DispatcherEvents.DriveAppInteraction, e);
    }

    function hideToast(e?: Event) {
        if (!toastEl) {
            return;
        }

        if (document.activeElement === toastEl) {
            return;
        }

        logInteraction(e);

        toastEl.classList.remove("show");
        if (map && map.link && map.link.length > 0 && toastLinks && toastLinks.length > 0) {
            toastLinks.forEach((linkSpan, index) => {
                let toastLinkObj = map.link && map.link[index];
                if (toastLinkObj && toastLinkObj.callbackWrapper) {
                    linkSpan && linkSpan.removeEventListener("click", toastLinkObj.callbackWrapper, false);
                    delete toastLinkObj.linkCallback;
                    delete toastLinkObj.callbackWrapper;
                }
            });
        }

        setTimeout(() => {
            if (toastEl) {
                toastEl.style.display = "none";
                removeToast(toastEl);
                options.onHidden && options.onHidden();
            }
            toastEl = null;
        }, 600);
    }

    function delayedHideToast() {
        if (options.timeOut > 0 || options.extendedTimeOut > 0) {
            intervalId = setTimeout(hideToast, options.extendedTimeOut);
        }
    }

    function handleEvents() {
        if (!toastEl) {
            return;
        }

        if (options.closeOnHover) {
            toastEl.addEventListener("mouseover", stickAround);
            toastEl.addEventListener("mouseout", delayedHideToast);
        }

        if (!options.onclick && options.tapToDismiss) {
            toastEl.onclick = hideToast;
        }

        if (options.onclick) {
            toastEl.onclick = function (event) {
                options.onclick && options.onclick(event);
                hideToast();
            };
        }
    }

    function hideExistingToastByOrigin(origin: string) {
        let existingToasts = document.querySelectorAll(`[data-origin="${ origin }"]`);
        Array.prototype.filter.call(existingToasts, (toast: HTMLElement) => toast.style.display = "none");
    }

    function displayToast() {
        if (!toastEl) {
            return;
        }

        if (options.origin) {
            hideExistingToastByOrigin(options.origin);
        }

        toastEl.style.display = "block";
        setTimeout(() => {
            toastEl && toastEl.classList.add("show");
        }, 1);

        if (options.timeOut > 0) {
            intervalId = setTimeout(hideToast, options.timeOut);
        }
    }

    function setAdditionalClass() {
        if (!(options.toastAdditionalClass && toastEl)) {
            return;
        }
        toastEl.classList.add(options.toastAdditionalClass);
    }

    function setIcon() {
        if (!(iconClass && toastEl)) {
            return;
        }
        toastEl.classList.add(iconClass);
    }

    function setSequence() {
        if (!toastEl) {
            return;
        }
        if (options.newestOnTop) {
            containerEl && containerEl.insertBefore(toastEl, containerEl.firstElementChild);
        } else {
            containerEl && containerEl.appendChild(toastEl);
        }
    }

    function setTitle() {
        if (!map.title || !toastEl || !titleEl) {
            return;
        }
        let suffix = map.title;

        if (typeof (suffix) === "string" || suffix instanceof String) {
            titleEl.innerHTML = htmlEscape(suffix as string);
        } else if (suffix instanceof HTMLElement) {
            titleEl.appendChild(suffix);
        } else if (suffix instanceof Array) {
            suffix.forEach(s => titleEl && titleEl.appendChild(s));
        }
        titleEl.classList.add(options.titleClass);
        toastEl.appendChild(titleEl);
    }

    function setMessage() {
        if (!map.message || !messageEl || !toastEl) {
            return;
        }
        let suffix = map.message;

        if (typeof (suffix) === "string" || suffix instanceof String) {
            if (options.allowHtml) {
                messageEl.innerHTML = suffix as string;
            } else {
                messageEl.innerHTML = htmlEscape(suffix as string);
            }
        } else if (suffix instanceof HTMLElement) {
            messageEl.appendChild(suffix);
        } else if (suffix instanceof Array) {
            suffix.forEach(s => messageEl && messageEl.appendChild(s));
        }
        messageEl.classList.add(options.messageClass);
        link && messageEl.classList.add("toast-message-link");
        toastEl.appendChild(messageEl);
    }

    function setLink() {
        if (!map.link || !toastEl || !linkEl) {
            return;
        }
        let links = map.link;
        toastLinks = [];

        links.forEach(toastLinkObj => {
            let linkSpan = createElement("a");
            if (toastLinkObj.linkId) {
                linkSpan.id = toastLinkObj.linkId;
            }
            linkSpan.innerHTML = htmlEscape(toastLinkObj.linkText);
            linkSpan.setAttribute("href", "javascript:void(0)");
            if (toastLinkObj.linkCallback) {
                toastLinkObj.callbackWrapper = e => {
                    logInteraction(e);
                    toastLinkObj.linkCallback && toastLinkObj.linkCallback();
                };
                linkSpan.addEventListener("click", toastLinkObj.callbackWrapper, false);
            }
            toastLinks.push(linkSpan);
            linkEl?.appendChild(linkSpan);
        });
        linkEl.classList.add("toast-link");
        if (links.length > 1) {
            linkEl.classList.add("multi-button");
        } else {
            linkEl.classList.add("single-button");
        }
        toastEl.appendChild(linkEl);
    }

    function personalizeToast() {
        setAdditionalClass();
        setIcon();
        setTitle();
        setMessage();
        setLink();
        setSequence();
        setAria();
        setOrigin();
    }

    if (!containerEl) {
        createContainer(options);
    }
    setToastrPosition(options);

    personalizeToast();

    displayToast();

    handleEvents();

    return toastEl;
};

interface IExport {
    error: TToastrDisplayMethod;
    info: TToastrDisplayMethod;
    success: TToastrDisplayMethod;
    warning: TToastrDisplayMethod;
    remove: (el: HTMLElement) => void;
}

export default {
    error: (message, title, link, options) => {
        let defaultOptions: Partial<IToastrOptions> = { preventDuplicates: true };
        !!options && (defaultOptions = extend({}, defaultOptions, options));
        return notify("error", message, title, link, defaultOptions);
    },
    info: (message, title, link, options) => notify("info", message, title, link, options),
    success: (message, title, link, options) => notify("success", message, title, link, options),
    warning: (message, title, link, options) => notify("warning", message, title, link, options),
    remove: removeToast
} as IExport;